import React, { useEffect, useState } from "react";
import format from "date-fns/format";
import { Button, Popover, Space, Tooltip } from "antd";

import {
  IFulfilledChat,
  InviteStatus,
  MessageTypeNames,
  TMessage,
  IMessageInviteBody,
  IMessageTextBody,
  ChatStatus
} from "../../../interfaces/interfacesChat";
import { useContextHospi } from "../../../context/ContextHospi";



import { prepareMessageText } from "../utils";
import {
  AvatarImage,
  AvatarPlaceholder,
  EditedMessageIndicator,
  FullName,
  IconDelivered,
  IconPending,
  IconRead,
  IndicatorContainer,
  MessageMenuIcon,
  MessageTime,
  RightSide,
  Root,
  Text,
  TextContainer,
  TextContainerInner
} from "./MessagesItemStyles";
import { HostPendingInviteMessage } from "./SystemMessages/HostPendingInviteMessage";
import { StudentPendingInviteMessage } from "./SystemMessages/StudentPeindingInviteMessage";
import { HostAcceptedInviteMessage } from "./SystemMessages/HostAcceptedInviteMessage";
import { StudentAcceptedInviteMessage } from "./SystemMessages/StudentAcceptedInviteMessage";
import { SystemMessage } from "./SystemMessages/SystemMessage";
import { parseServerDateTime } from "utils/time";
import { useTranslation } from "react-i18next";

interface IMessageItem {
  chat: IFulfilledChat;
  message: TMessage;
  previousMessage?: TMessage;
  onRendered?: (message: TMessage) => void;
  onEdit: (message: TMessage) => void;
  onDelete: (message: TMessage) => void;
  isCreating?: boolean;
  isDeleting?: boolean;
  isUpdating?: boolean;
}

export const MessageItem = React.memo<IMessageItem>(
  ({
    chat,
    message,
    previousMessage,
    onRendered,
    onEdit,
    onDelete,
    isCreating,
    isDeleting,
    isUpdating
  }) => {
    const { userProfile } = useContextHospi();
    const [tooltipIsOpened, setTooltipIsOpened] = useState(false);
    const [actionsIsOpened, setActionsIsOpened] = useState(false);
    const { t } = useTranslation("chats");

    useEffect(() => {
      onRendered?.(message);
    }, []);
    const messageText =
      (message.body as IMessageTextBody)?.text ??
      "[" + JSON.stringify(message.body) + "]";

    const isMyMessage = message.author_id === userProfile?.user_id;
    const isEdited =
      message.created_at !== message.updated_at && !message.deleted;
    const date = parseServerDateTime(message.created_at);

    const isPreviousMessageSameAuthor =
      message.author_id === previousMessage?.author_id;

    if (message.msg_type === MessageTypeNames.REMOVE_TENANT) {
      return userProfile?.user_type === "host" ? (
        <SystemMessage
          content={t("systemMessages.coreMessages.cancelledInvite")}
          date={date}
        />
      ) : (
        <SystemMessage
          content={t("systemMessages.coreMessages.hostCancelledInvite")}
          date={date}
        />
      );
    }

    if (
      message.msg_type === MessageTypeNames.REMOVE_INTEREST ||
      message.msg_type === MessageTypeNames.REMOVE_CHAT ||
      message.msg_type === MessageTypeNames.UNREMOVE_CHAT ||
      message.msg_type === MessageTypeNames.RENT_DATES_UPDATED
    ) {
      return <SystemMessage content={messageText} date={date} />;
    }

    if (message.msg_type === MessageTypeNames.INVITE_MSG) {
      const inviteMessageStatus = (message.body as IMessageInviteBody)
        .invite_msg_status;

      if (
        inviteMessageStatus === InviteStatus.CREATED ||
        inviteMessageStatus === InviteStatus.INVITE_DATES_UPDATED
      ) {
        return userProfile?.user_type === "host" ? (
          <HostPendingInviteMessage chat={chat} message={message} />
        ) : (
          <StudentPendingInviteMessage chat={chat} message={message} />
        );
      }

      if (inviteMessageStatus === InviteStatus.ACCEPTED) {
        return userProfile?.user_type === "host" ? (
          <HostAcceptedInviteMessage t={t} message={message} chat={chat} />
        ) : (
          <StudentAcceptedInviteMessage t={t} />
        );
      }

      if (inviteMessageStatus === InviteStatus.REJECTED) {
        return userProfile?.user_type === "host" ? (
          <SystemMessage
            title={t("systemMessages.coreMessages.genericCancel")}
            date={parseServerDateTime(message.created_at)}
          />
        ) : (
          <SystemMessage
            title={t("systemMessages.coreMessages.genericCancel")}
            date={parseServerDateTime(message.created_at)}
          />
        );
      }

      if (inviteMessageStatus === InviteStatus.REMOVED) {
        return userProfile?.user_type === "host" ? (
          <SystemMessage
            title={t("systemMessages.coreMessages.genericRemove")}
            date={parseServerDateTime(message.created_at)}
          />
        ) : (
          <SystemMessage
            title={t("systemMessages.coreMessages.genericRemove")}
            date={parseServerDateTime(message.created_at)}
          />
        );
      }
    }

    return (
      <Root
        isMyMessage={isMyMessage}
        isTemporary={isCreating || isDeleting || isUpdating}
      >
        {!isMyMessage ? (
          !isPreviousMessageSameAuthor ? (
            <AvatarImage src={chat.conversation_partner_short?.thumbnail_url} />
          ) : (
            <AvatarPlaceholder />
          )
        ) : null}
        <RightSide>
          <span>
            {!isMyMessage && !isPreviousMessageSameAuthor ? (
              <FullName>{`${chat.conversation_partner_short?.first_name} ${chat.conversation_partner_short?.last_name}`}</FullName>
            ) : null}
            {!isPreviousMessageSameAuthor ? (
              <MessageTime isDetached={false}>
                {format(date, "HH:mm")}
              </MessageTime>
            ) : null}
          </span>

          <TextContainer>
            <Tooltip
              placement={isMyMessage ? "left" : "right"}
              mouseEnterDelay={0}
              mouseLeaveDelay={0}
              destroyTooltipOnHide={true}
              open={tooltipIsOpened || actionsIsOpened}
              onOpenChange={(visible) => {
                setTooltipIsOpened(visible);
              }}
              title={
                <div>
                  <div>{format(date, "HH:mm")}</div>
                </div>
              }
            >
              <TextContainerInner>
                {!message.deleted ? (
                  <Text isMyMessage={isMyMessage} isEdited={isEdited}>
                    {prepareMessageText(messageText)}
                    {isEdited ? (
                      <Tooltip
                        placement={"bottom"}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                        destroyTooltipOnHide={true}
                        title={t("systemMessages.coreMessages.isEdited")}
                      >
                        <EditedMessageIndicator />
                      </Tooltip>
                    ) : null}
                  </Text>
                ) : (
                  <Text isMyMessage={isMyMessage} deleted={true}>
                    {t("deletedMessage")}
                  </Text>
                )}
                {isMyMessage && !message.deleted ? (
                  <>
                    {isCreating || isDeleting || isUpdating ? (
                      <IndicatorContainer isPending={true}>
                        <IconPending />
                      </IndicatorContainer>
                    ) : null}

                    {!(isCreating || isDeleting || isUpdating) &&
                      !message.is_read ? (
                      <IndicatorContainer>
                        <IconDelivered />
                      </IndicatorContainer>
                    ) : null}

                    {!(isCreating || isDeleting || isUpdating) &&
                      message.is_read ? (
                      <IndicatorContainer>
                        <IconRead />
                      </IndicatorContainer>
                    ) : null}
                  </>
                ) : null}

                {isMyMessage && !message.deleted && chat.chat_status === ChatStatus.ACCEPTED ? (
                  <Popover
                    placement="bottom"
                    content={
                      <>
                        <Space.Compact direction="vertical">
                          {chat.chat_status === ChatStatus.ACCEPTED ? (
                            <Button
                              type="default"
                              onClick={() => {
                                setTooltipIsOpened(false);
                                setActionsIsOpened(false);
                                onEdit(message);
                              }}
                            >
                              {t("systemMessages.coreMessages.edit")}
                            </Button>

                          ) : null}
                          <Button
                            type="primary"
                            onClick={() => {
                              setTooltipIsOpened(false);
                              setActionsIsOpened(false);
                              onDelete(message);
                            }}
                            danger
                          >
                            {t("systemMessages.coreMessages.delete")}
                          </Button>
                        </Space.Compact>
                      </>
                    }
                    trigger="click"
                    open={actionsIsOpened}
                    onOpenChange={(visible) => {
                      setActionsIsOpened(visible);
                    }}
                  >
                    <MessageMenuIcon
                      visible={tooltipIsOpened || actionsIsOpened}
                    />
                  </Popover>
                ) : null}
              </TextContainerInner>
            </Tooltip>
          </TextContainer>
        </RightSide>
      </Root>
    );
  }
);
